import Promise from 'bluebird';
import { some, pick } from 'underscore';

/*
 * Captures:
 * [0]: whole match
 * [1]: location level text
 * [2]: default value
 */
const locationRuleSyntax = /\{location\((city|state|country)\)(?:}|:([^{}]+)})/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: location level text (could be incomplete)
 * [2]: default value (could be empty)
 */
const locationRuleInProgressSyntax = /\{l(?:o|$)(?:c|$)(?:a|$)(?:t|$)(?:i|$)(?:o|$)(?:n|$)(?:\(|$)((?:c|$)(?:i|$)(?:t|$)(?:y|$)|(?:s|$)(?:t|$)(?:a|$)(?:t|$)(?:e|$)|(?:c|$)(?:o|$)(?:u|$)(?:n|$)(?:t|$)(?:r|$)(?:y|$))(?:\)|$)(?::|$)([^{}]*)/i;

/**
 * Looks for location rules in given text and substitutes them with their values.
 * {location(city): Seattle} => Seattle
 * @param {string} text - input text
 * @returns {string} text after substitutions
 */
export function evaluatelocationRules(text) {
  if (text && text.length > 0) {
    const locationReplaceSyntax = new RegExp(locationRuleSyntax.source, 'gi');

    return Promise.resolve(text.replace(locationReplaceSyntax, (match, p1, p2) => {
      if (p2) return p2;
      return match;
    }));
  }
  return Promise.resolve(text);
}

/**
 * Looks for a keyword rule definition in given text starting from passed index.
 *   If found, it parses the definition.
 * @param {string} text - text to search in
 * @param {string} syntax - regex syntax use for matching
 * @param {int} startIndex - starts searching at this index
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseLocationRule(text, syntax, startIndex, cursorIndex) {
  const tokens = syntax.exec(text);

  if ((tokens === null) ||
    (tokens.length !== 3) ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  return {
    source: tokens[0],
    defaultText: tokens[2],
    locationFormat: tokens[1],
  };
}

/**
 * Parses out properties to initialize a keyword helper using current cursor position
 * @param {string} text - input text
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} properties to initialize a keyword helper
 *   if cursor is placed within a valid definition or at the end of a partial definition.
 * Returns undefined; otherwise.
 */
export function parseRule(text, cursorIndex) {
  const processedText = text || '';
  const startIndex = processedText.lastIndexOf('{', cursorIndex);

  if (startIndex === -1 || cursorIndex === startIndex) {
    return null;
  }

  const parsers = [
    {
      input: processedText.substring(startIndex),
      syntax: locationRuleSyntax,
      inclusive: false,
    },
    {
      input: processedText.slice(startIndex, cursorIndex),
      syntax: locationRuleInProgressSyntax,
      inclusive: true,
    },
  ];

  let properties = null;

  some(parsers, (parser) => {
    const rule = parseLocationRule(parser.input, parser.syntax, startIndex, cursorIndex);

    if ((rule !== null) &&
      (cursorIndex < startIndex + rule.source.length + (parser.inclusive ? 1 : 0))) {
      const options = pick(rule, 'defaultText', 'locationFormat');

      options.prefix = processedText.substring(0, startIndex);
      options.suffix = processedText.substring(startIndex + rule.source.length);
      options.startIndex = startIndex;
      options.endIndex = startIndex + rule.source.length;

      properties = {
        type: 'location',
        options,
      };

      return true;
    }
    return false;
  });

  return properties;
}
